import { globalsiteConfig } from "@/aips/global";
import { isNosugarBrowser, NOSUGAR_CONFIG } from "@/utils/nosugar";

let configData = new Map(); // 用于缓存配置数据
let isFetching = false; // 防止并发请求
let siteConfigReadyPromise = null; // 用于全局等待 siteConfig
const SITE_CONFIG_CACHE_PREFIX = "siteConfig:";

const buildSiteConfigCacheKey = ({ domain = "", port = "" } = {}) =>
  `${SITE_CONFIG_CACHE_PREFIX}${domain}:${port}`;

const getWebSiteConfigRequest = () => ({
  domain: window.location.hostname,
  port: window.location.port,
});

const getNosugarSiteConfigRequest = () => {
  return {
    domain: NOSUGAR_CONFIG.APP_ID,
    port: "",
  };
};

const getSiteConfigRequestData = () =>
  isNosugarBrowser() ? getNosugarSiteConfigRequest() : getWebSiteConfigRequest();

const getCachedSiteConfig = (cacheKey) => {
  const cachedConfig = sessionStorage.getItem(cacheKey);
  if (cachedConfig) {
    return cachedConfig;
  }

  const legacyCache =
    cacheKey === buildSiteConfigCacheKey(getWebSiteConfigRequest())
      ? sessionStorage.getItem("siteConfig")
      : null;

  return legacyCache;
};

const restoreCachedSiteConfig = (cachedConfig, cacheKey) => {
  try {
    configData = new Map(Object.entries(JSON.parse(cachedConfig)));
    return true;
  } catch (error) {
    sessionStorage.removeItem(cacheKey);
    sessionStorage.removeItem("siteConfig");
    console.error("读取站点配置缓存失败:", error);
    return false;
  }
};

/**
 * 获取站点配置，并缓存到 configData，避免重复请求
 */
const getsiteConfig = async () => {
  if (configData.size > 0) return;

  const siteData = getSiteConfigRequestData();
  const cacheKey = buildSiteConfigCacheKey(siteData);
  const cachedConfig = getCachedSiteConfig(cacheKey);
  if (cachedConfig && restoreCachedSiteConfig(cachedConfig, cacheKey)) {
    window.dispatchEvent(new Event("siteConfigReady"));
    return;
  }

  if (isFetching) return;
  isFetching = true;

  try {
    const res = await globalsiteConfig(siteData);
    if (res?.data?.detailVo) {
      configData.clear();
      res.data.detailVo.forEach((item) => {
        configData.set(item.configKey, item.configValue);
      });

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify(Object.fromEntries(configData))
      );
    }
  } catch (error) {
    console.error("获取站点配置失败:", error);
    // 允许后续 waitForSiteConfig 再次发起请求
    siteConfigReadyPromise = null;
  } finally {
    isFetching = false;
    if (configData.size === 0) {
      siteConfigReadyPromise = null;
    }
    // 请求失败时也结束本轮等待，让页面使用本地兜底资源
    window.dispatchEvent(new Event("siteConfigReady"));
  }
};

/**
 * 等待站点配置准备好（适合懒加载页面）
 */
const waitForSiteConfig = () => {
  if (configData.size > 0) return Promise.resolve();

  if (!siteConfigReadyPromise) {
    siteConfigReadyPromise = new Promise((resolve) => {
      window.addEventListener("siteConfigReady", () => resolve(), {
        once: true,
      });
    });

    // 保险起见，主动触发一次请求
    getsiteConfig();
  }

  return siteConfigReadyPromise;
};

/**
 * 获取字段值
 */
const getConfigValue = (key, defaultValue = null) => {
  if (configData.size === 0) {
    const cachedData = getCachedSiteConfig(
      buildSiteConfigCacheKey(getSiteConfigRequestData())
    );
    if (cachedData) {
      restoreCachedSiteConfig(
        cachedData,
        buildSiteConfigCacheKey(getSiteConfigRequestData())
      );
    }
  }

  return configData.get(key) ?? defaultValue;
};

/**
 * 刷新站点配置（清除缓存并重新请求）
 */
const refreshSiteConfig = async () => {
  const cacheKey = buildSiteConfigCacheKey(getSiteConfigRequestData());

  // 清除缓存
  configData.clear();
  sessionStorage.removeItem("siteConfig");
  sessionStorage.removeItem(cacheKey);

  // 重置状态
  isFetching = false;
  siteConfigReadyPromise = null;

  // 重新获取配置
  await getsiteConfig();
};

export {
  getSiteConfigRequestData,
  getsiteConfig,
  getConfigValue,
  waitForSiteConfig,
  refreshSiteConfig, // ✅ 新增导出
};
